import { Loading } from 'components'
import { FinalCodeResult, TestCase } from 'features/AlgoEditor'
import React, { useMemo } from 'react'
import { useAlgoEditorState } from 'state'
import { b64_to_utf8 } from 'utils'
import formatBytes from 'utils/formatBytes'

import { CodeIcon } from '@heroicons/react/solid'
import { tx } from '@twind/core'

function ResultTab({
  loading,
  finalSubmissionResponse
}: {
  finalSubmissionResponse: FinalCodeResult | undefined
  loading: boolean
}) {
  const { isCustomTestCases, isPolling } = useAlgoEditorState()
  const finalSubmitTestCaseFaild = useMemo(() => {
    if (
      finalSubmissionResponse &&
      finalSubmissionResponse.is_submitted &&
      !isPolling
    ) {
      let value: TestCase = {
        stdin: '',
        stdout: '',
        stderr: '',
        expected_output: '',
        time: '',
        memory: 0,
        status_id: 0,
        status_description: '',
        compile_output: ''
      }

      Object.keys(finalSubmissionResponse.test_cases).forEach((obj) => {
        if (
          finalSubmissionResponse.test_cases[obj].status_id &&
          finalSubmissionResponse.test_cases[obj].status_description !==
            'Accepted'
        ) {
          value = finalSubmissionResponse.test_cases[obj]
          return ''
        }
      })
      return value
    }
    return undefined
  }, [finalSubmissionResponse, isPolling])

  return (
    <>
      {loading ? (
        <Loading className="my-4" />
      ) : !finalSubmissionResponse ? (
        <div className="my-16 flex flex-row justify-center items-center gap-2">
          <div>
            <CodeIcon className="text-white h-7 w-7" />
          </div>
          <div className="text-center text-2xl text-green-500">
            Submit code to see the your result here
          </div>
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center px-2">
          <div className="flex flex-row justify-between items-center py-4">
            <div className="flex justify-start items-center space-x-6 mt-3">
              {!isCustomTestCases && (
                <div
                  className={tx(
                    'capitalize text-lg font-semibold',
                    finalSubmissionResponse.status === 'Accepted'
                      ? 'text-green-500'
                      : finalSubmissionResponse.status === 'Pending'
                      ? 'text-yellow-500'
                      : 'text-red-500'
                  )}>
                  {finalSubmissionResponse.status}
                </div>
              )}

              <div className="text-zinc-400 capitalize">
                {finalSubmissionResponse.language}
              </div>

              <div className="text-zinc-400">
                Run Time:{' '}
                {finalSubmissionResponse.total_runtime
                  ? finalSubmissionResponse.total_runtime.slice(0, 5) + 's'
                  : '-'}
              </div>

              <div className="text-zinc-400">
                Memory:{' '}
                {finalSubmissionResponse.total_memory
                  ? formatBytes(parseInt(finalSubmissionResponse.total_memory))
                  : '-'}
              </div>

              {!isCustomTestCases && (
                <div>
                  Test Cases:
                  <span className="ml-2 font-medium">{`${finalSubmissionResponse.passed_test_cases}/${finalSubmissionResponse.total_test_cases}`}</span>
                </div>
              )}
            </div>
          </div>

          {!finalSubmissionResponse.is_submitted ? (
            <div className="w-full">
              {Object.keys(finalSubmissionResponse.test_cases).map((key) => (
                <div
                  key={key}
                  className="flex flex-col justify-center w-full space-x-1 space-y-2">
                  {finalSubmissionResponse.test_cases[key].compile_output ? (
                    <div className="bg-zinc-800 rounded py-2 px-2 min-w-full w-max text-red-500 overflow-x-auto">
                      <pre>
                        {b64_to_utf8(
                          finalSubmissionResponse.test_cases[key]
                            .compile_output || ''
                        )}
                      </pre>
                    </div>
                  ) : (
                    <>
                      {finalSubmissionResponse.test_cases[key].stderr && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Stderr</div>
                          <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[85%] overflow-x-auto">
                            <pre>
                              {b64_to_utf8(
                                finalSubmissionResponse.test_cases[key].stderr
                              ) || ''}
                            </pre>
                          </div>
                        </div>
                      )}

                      {!isCustomTestCases && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Stdin</div>

                          {finalSubmissionResponse.test_cases[key].stdin && (
                            <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[85%] overflow-x-auto">
                              <pre>
                                {b64_to_utf8(
                                  finalSubmissionResponse.test_cases[key].stdin
                                ) || ''}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}

                      {!finalSubmissionResponse.test_cases[key].stderr && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Stdout</div>

                          {finalSubmissionResponse.test_cases[key].stdout && (
                            <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[70%] overflow-x-auto">
                              <pre>
                                {b64_to_utf8(
                                  finalSubmissionResponse.test_cases[key].stdout
                                ) || ''}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}

                      {!isCustomTestCases && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Expected Output</div>

                          {finalSubmissionResponse.test_cases[key]
                            .expected_output && (
                            <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[70%] overflow-x-auto">
                              <pre>
                                {b64_to_utf8(
                                  finalSubmissionResponse.test_cases[key]
                                    .expected_output
                                ) || ''}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            finalSubmitTestCaseFaild && (
              <div className="w-full">
                <div className="flex flex-col justify-center w-full space-x-1 space-y-2">
                  {finalSubmitTestCaseFaild?.compile_output ? (
                    <div className="bg-zinc-800 rounded py-2 px-2 min-w-full w-max text-red-500 overflow-x-auto">
                      <pre>
                        {b64_to_utf8(
                          finalSubmitTestCaseFaild.compile_output || ''
                        )}
                      </pre>
                    </div>
                  ) : (
                    <>
                      {finalSubmitTestCaseFaild?.stderr && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Stderr</div>
                          <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[85%] overflow-x-auto">
                            <pre>
                              {b64_to_utf8(finalSubmitTestCaseFaild.stderr) ||
                                ''}
                            </pre>
                          </div>
                        </div>
                      )}

                      {finalSubmitTestCaseFaild?.stdin && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Stdin</div>
                          <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[85%] overflow-x-auto">
                            <pre>
                              {b64_to_utf8(finalSubmitTestCaseFaild.stdin) ||
                                ''}
                            </pre>
                          </div>
                        </div>
                      )}
                      {finalSubmitTestCaseFaild.stdout && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Stdout</div>

                          <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[70%] overflow-x-auto">
                            <pre>
                              {b64_to_utf8(finalSubmitTestCaseFaild.stdout) ||
                                ''}
                            </pre>
                          </div>
                        </div>
                      )}
                      {finalSubmitTestCaseFaild?.expected_output && (
                        <div className="flex flex-row items-center justify-between space-x-2.5">
                          <div className="w-[15%]">Expected Output</div>

                          {finalSubmitTestCaseFaild?.expected_output && (
                            <div className="bg-zinc-800 rounded py-2 px-2 flex-1 w-[70%] overflow-x-auto">
                              <pre>
                                {b64_to_utf8(
                                  finalSubmitTestCaseFaild.expected_output
                                ) || ''}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </>
  )
}

export default ResultTab
